/* eslint-disable import/no-duplicates */
import { useState } from 'react';
import {
  Button,
  Icon,
  useDisclosure,
  ModalBody,
  ModalFooter,
  Box,
  Text,
  VStack,
  HStack,
  Divider,
  useClipboard,
} from '@chakra-ui/react';
import { RiMailSendLine, RiFileCopyLine, RiCheckLine } from 'react-icons/ri';
import { formatReportDate } from '../../../../utils/formatReportDate';

import { ModalComponent } from '..';
import { IExpenseReport } from '../../../../pages/Dashboard/vehicles_management/Expenses';
import { buildSignatureEmailData } from '../../../../utils/sendSignatureEmail';
import { expenseReportsService } from '../../../../services/expenseReportsService';
import { apllyToast } from '../../../Global/Toast2.0';

interface IModalProps {
  report: IExpenseReport;
  onStatusUpdate: () => void;
  isManager?: boolean;
}

export function ModalSendForSignature({
  report,
  onStatusUpdate,
  isManager = false,
}: IModalProps) {
  const { onOpen, isOpen, onClose } = useDisclosure();
  const [signatureUrl, setSignatureUrl] = useState('');
  const [directorEmail, setDirectorEmail] = useState('');
  const [directorName, setDirectorName] = useState('');
  const [sending, setSending] = useState(false);
  const { hasCopied, onCopy } = useClipboard(signatureUrl);

  const handleOpen = () => {
    try {
      if (isManager) {
        // Para gestor: usar dados do manager
        const managerEmail = report.manager?.email;
        const managerName = report.manager?.name || 'Gestor';
        if (!managerEmail) {
          throw new Error(
            'O gestor não possui email cadastrado. Atualize o cadastro.',
          );
        }
        const baseUrl = window.location.origin;
        const url = `${baseUrl}/sign-expense/${report.id}?role=manager`;
        setSignatureUrl(url);
        setDirectorEmail(managerEmail);
        setDirectorName(managerName);
      } else {
        const data = buildSignatureEmailData({
          report,
          senderName: 'Recepção SAMASC',
        });
        setSignatureUrl(data.signatureUrl);
        setDirectorEmail(data.directorEmail);
        setDirectorName(data.directorName);
      }
      onOpen();
    } catch (err: any) {
      apllyToast('error', err.message || 'Erro ao preparar email');
    }
  };

  const handleSendEmail = async () => {
    setSending(true);
    try {
      const url = signatureUrl;
      const email = directorEmail;
      const name = directorName;
      const subject = isManager
        ? 'Aprovação de Relatório de Despesas e Reembolsos'
        : 'Solicitação de Assinatura Digital - Relatório de Despesas e Reembolsos';

      if (isManager) {
        await expenseReportsService.sendManagerSignatureEmail(report.id, {
          url,
          email,
          name,
          subject,
        });
      } else {
        const data = buildSignatureEmailData({
          report,
          senderName: 'Recepção SAMASC',
        });
        await expenseReportsService.sendSignatureEmail(report.id, {
          url: data.signatureUrl,
          email: data.directorEmail,
          name: data.directorName,
          subject: data.subject,
          message: data.message,
        });
      }

      onStatusUpdate();

      apllyToast(
        'success',
        `Email enviado para ${name}! ${
          isManager ? 'Aguardando aprovação.' : 'Aguardando assinatura.'
        }`,
      );

      onClose();
    } catch (err: any) {
      console.error('Erro ao enviar email:', err);
      apllyToast(
        'error',
        err.response?.data?.message || err.message || 'Erro ao enviar email',
      );
    } finally {
      setSending(false);
    }
  };

  const handleCopyLink = () => {
    onCopy();
    apllyToast('success', 'Link copiado para área de transferência!');
  };

  return (
    <Box display="inline-block">
      <Button
        size="sm"
        colorScheme={isManager ? 'teal' : 'purple'}
        variant="ghost"
        onClick={handleOpen}
        title={
          isManager
            ? 'Enviar para Aprovação do Gestor'
            : 'Enviar para Assinatura Digital'
        }
      >
        <Icon as={RiMailSendLine} fontSize="20" />
      </Button>

      <ModalComponent
        title={
          isManager
            ? 'Enviar para Aprovação do Gestor'
            : 'Enviar para Assinatura Digital'
        }
        isOpen={isOpen}
        onClose={onClose}
        sizeSpecifyc="xl"
      >
        <ModalBody>
          <VStack spacing={4} align="stretch">
            {/* Informações do Relatório */}
            <Box bg="whiteAlpha.100" p={4} borderRadius="md">
              <Text fontWeight="bold" mb={3} color="gray.300" fontSize="sm">
                {isManager
                  ? 'Relatório a ser aprovado'
                  : 'Relatório a ser assinado'}
              </Text>
              <HStack spacing={6} mb={3}>
                <Box>
                  <Text fontSize="xs" color="gray.500">
                    Usuário
                  </Text>
                  <Text fontWeight="medium" color="gray.200" fontSize="sm">
                    {directorName || report.director_name || '—'}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color="gray.500">
                    Data
                  </Text>
                  <Text color="gray.200" fontSize="sm">
                    {formatReportDate(report.date, 'dd/MM/yyyy')}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color="gray.500">
                    Itens
                  </Text>
                  <Text color="gray.200" fontSize="sm">
                    {report.items.length}{' '}
                    {report.items.length === 1 ? 'despesa' : 'despesas'}
                  </Text>
                </Box>
              </HStack>

              {directorEmail && (
                <Box
                  mt={2}
                  p={2}
                  bg="whiteAlpha.100"
                  borderRadius="md"
                  borderLeft="3px solid"
                  borderLeftColor="purple.400"
                >
                  <Text fontSize="xs" color="gray.500">
                    Email do destinatário
                  </Text>
                  <Text fontSize="sm" fontWeight="medium" color="purple.300">
                    {directorEmail}
                  </Text>
                </Box>
              )}
            </Box>

            <Divider borderColor="whiteAlpha.200" />

            {/* Link de Assinatura */}
            <Box>
              <Text fontWeight="bold" mb={2} color="gray.300" fontSize="sm">
                Link de Assinatura
              </Text>
              <Box bg="whiteAlpha.100" p={3} borderRadius="md">
                <Text
                  fontSize="xs"
                  fontFamily="mono"
                  color="blue.300"
                  wordBreak="break-all"
                >
                  {signatureUrl}
                </Text>
              </Box>

              <Button
                mt={3}
                leftIcon={
                  <Icon as={hasCopied ? RiCheckLine : RiFileCopyLine} />
                }
                colorScheme={hasCopied ? 'green' : 'blue'}
                variant="outline"
                size="sm"
                onClick={handleCopyLink}
                width="full"
              >
                {hasCopied ? 'Link Copiado!' : 'Copiar Link'}
              </Button>
            </Box>

            <Divider borderColor="whiteAlpha.200" />

            {/* Instruções */}
            <Box
              bg="whiteAlpha.100"
              p={3}
              borderRadius="md"
              borderLeft="3px solid"
              borderLeftColor="blue.400"
            >
              <Text fontSize="xs" color="gray.400" mb={2} fontWeight="bold">
                Como funciona:
              </Text>
              <VStack align="start" spacing={1}>
                <Text fontSize="xs" color="gray.400">
                  1. Ao clicar em &quot;Enviar Email&quot;, o sistema enviará
                  automaticamente para o email do usuário
                </Text>
                <Text fontSize="xs" color="gray.400">
                  2. O usuário receberá o link para assinar digitalmente
                </Text>
                <Text fontSize="xs" color="gray.400">
                  3. Você também pode copiar o link e compartilhar por outro
                  canal
                </Text>
              </VStack>
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose} isDisabled={sending}>
            Cancelar
          </Button>
          <Button
            colorScheme="purple"
            leftIcon={<Icon as={RiMailSendLine} />}
            onClick={handleSendEmail}
            isLoading={sending}
            loadingText="Enviando..."
          >
            Enviar Email
          </Button>
        </ModalFooter>
      </ModalComponent>
    </Box>
  );
}
